// Flow 3 - brand direction picker (docs/phase-0/slice5-ux-flows.md §6.1-6.2; route
// `/apps/new/brand`, a `[wizard]` screen per the ux spec's route map, §2.1). Requires
// `verdict IN {passed, unvalidated}` (D6 sequence guard, gate.ts's `canEnterBrand`) - a
// stale/direct URL hit on the guard redirects to the verdict/glim screen rather than
// erroring (ux spec §6.2 "Denied (sequence)"). The FIRST of the two default-deny
// approval gates in this flow: "Choose this direction" is a deliberate, explicit
// confirm, never an accidental commit (ux spec §6.1).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { AppView, BrandDirection } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { BrandDirectionCard } from "../../components/BrandDirectionCard.js";
import { generateBrandDirections, regenerateBrandDirections, approveBrandDirection } from "../../api/brand.js";
import { getApp } from "../../api/apps.js";
import { buildCopy } from "../../copy/build.js";
import { isViewerRole, useSession } from "../../state/session.js";
import { useWizard } from "../../state/wizard.js";
import { canEnterBrand } from "./gate.js";
import { setStoredBrandDirectionId } from "./brand-session.js";

const INCLUDED_ROUNDS = 2;

type DirectionsState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "regenerating"; readonly round: number }
  | { readonly kind: "results"; readonly directions: readonly BrandDirection[]; readonly round: number }
  | {
      readonly kind: "allowance_exhausted";
      readonly directions: readonly BrandDirection[];
      readonly round: number;
    }
  | { readonly kind: "chosen"; readonly directionId: string };

type ScreenState =
  | { readonly kind: "loading_app" }
  | { readonly kind: "app_error" }
  | { readonly kind: "sequence_denied" }
  | { readonly kind: "ready"; readonly app: AppView };

export const BrandDirectionPicker = (): React.JSX.Element => {
  const { state: session } = useSession();
  const { draft } = useWizard();
  const navigate = useNavigate();
  const [screenState, setScreenState] = useState<ScreenState>({ kind: "loading_app" });
  const [directionsState, setDirectionsState] = useState<DirectionsState>({ kind: "loading" });

  const appId = draft.appId;

  useEffect(() => {
    if (appId === null) return;
    const cancelled = { current: false };
    void (async () => {
      const result = await getApp(appId);
      if (cancelled.current) return;
      if (isErr(result)) {
        setScreenState({ kind: "app_error" });
        return;
      }
      setScreenState(
        canEnterBrand(result.value.verdict) ? { kind: "ready", app: result.value } : { kind: "sequence_denied" },
      );
    })();
    return () => {
      cancelled.current = true;
    };
  }, [appId]);

  useEffect(() => {
    if (screenState.kind !== "ready" || appId === null) return;
    const cancelled = { current: false };
    setDirectionsState({ kind: "loading" });
    void (async () => {
      const result = await generateBrandDirections(appId);
      if (cancelled.current) return;
      setDirectionsState(
        isErr(result) ? { kind: "error" } : { kind: "results", directions: result.value.directions, round: 1 },
      );
    })();
    return () => {
      cancelled.current = true;
    };
  }, [screenState.kind, appId]);

  if (appId === null || screenState.kind === "sequence_denied") {
    return <DirectionsRedirect appId={appId} />;
  }

  if (screenState.kind === "loading_app") {
    return (
      <FocusedFlowShell title={buildCopy("build.brand.directions.title")}>
        <div className="stack gap12">
          <div className="card pad skeleton-card" />
          <div className="card pad skeleton-card" />
        </div>
      </FocusedFlowShell>
    );
  }

  if (screenState.kind === "app_error") {
    return (
      <FocusedFlowShell
        title={buildCopy("build.brand.directions.title")}
        onBack={() => {
          void navigate("/apps");
        }}
      >
        <div className="card state-card">
          <p>We couldn&apos;t load this app. Try again.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setScreenState({ kind: "loading_app" });
            }}
          >
            Retry
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  const { app } = screenState;
  const viewerReadOnly = isViewerRole(session) ? buildCopy("build.brand.directions.deniedRole") : undefined;

  const handleChoose = (
    directionId: string,
    directions: readonly BrandDirection[],
    round: number,
  ): void => {
    setDirectionsState({ kind: "chosen", directionId });
    void (async () => {
      const result = await approveBrandDirection(app.id, directionId);
      if (isErr(result)) {
        setDirectionsState({ kind: "results", directions, round });
        return;
      }
      setStoredBrandDirectionId(directionId);
      void navigate("/apps/new/brand/mockups");
    })();
  };

  const handleRegenerate = (currentRound: number, currentDirections: readonly BrandDirection[]): void => {
    if (currentRound >= INCLUDED_ROUNDS) {
      setDirectionsState({ kind: "allowance_exhausted", directions: currentDirections, round: currentRound });
      return;
    }
    setDirectionsState({ kind: "regenerating", round: currentRound + 1 });
    void (async () => {
      const result = await regenerateBrandDirections(app.id);
      setDirectionsState(
        isErr(result)
          ? { kind: "error" }
          : { kind: "results", directions: result.value.directions, round: currentRound + 1 },
      );
    })();
  };

  return (
    <FocusedFlowShell
      title={buildCopy("build.brand.directions.title")}
      onBack={() => {
        void navigate("/apps/new/glim/verdict");
      }}
    >
      <h1 className="h2">{buildCopy("build.brand.directions.title")}</h1>

      {directionsState.kind === "loading" || directionsState.kind === "regenerating" ? (
        <div className="mt16" data-testid="directions-loading">
          <div className="meter bordered">
            <span style={{ width: "60%" }} />
          </div>
          <p className="muted mt8" role="status" style={{ fontSize: 12.5 }}>
            {directionsState.kind === "regenerating"
              ? buildCopy("build.brand.directions.regenerating")
              : buildCopy("build.brand.directions.loading", { appName: app.name })}
          </p>
        </div>
      ) : null}

      {directionsState.kind === "error" ? (
        <div className="card state-card mt16">
          <p>{buildCopy("build.brand.directions.error")}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setDirectionsState({ kind: "loading" });
            }}
          >
            {buildCopy("build.errors.generic.retryCta")}
          </button>
        </div>
      ) : null}

      {directionsState.kind === "results" || directionsState.kind === "allowance_exhausted" ? (
        <>
          {directionsState.round > 1 ? (
            <span className="chip mt8" style={{ display: "inline-flex" }}>
              {buildCopy("build.brand.directions.roundCounter", {
                round: directionsState.round,
                total: INCLUDED_ROUNDS,
              })}
            </span>
          ) : null}
          <div className="grid mt16" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
            {directionsState.directions.map((direction) => (
              <BrandDirectionCard
                key={direction.id}
                direction={direction}
                selected={false}
                onChoose={() => {
                  handleChoose(direction.id, directionsState.directions, directionsState.round);
                }}
                {...(viewerReadOnly !== undefined ? { disabledReason: viewerReadOnly } : {})}
              />
            ))}
          </div>
          {directionsState.kind === "allowance_exhausted" ? (
            <div className="banner amber mt16" role="status">
              <div className="grow">{buildCopy("build.brand.directions.allowanceExhausted")}</div>
              {viewerReadOnly === undefined ? (
                <button type="button" className="btn btn-dark btn-sm">
                  {buildCopy("build.brand.directions.addonCta")}
                </button>
              ) : null}
            </div>
          ) : (
            <div className="row gap12 mt16">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={viewerReadOnly !== undefined}
                onClick={() => {
                  handleRegenerate(directionsState.round, directionsState.directions);
                }}
              >
                Try another round
              </button>
            </div>
          )}
        </>
      ) : null}

      {directionsState.kind === "chosen" ? (
        <div className="card pad mt16" role="status">
          <p>{buildCopy("build.brand.directions.chosenConfirmation", { appName: app.name })}</p>
        </div>
      ) : null}
    </FocusedFlowShell>
  );
};

/** A stale/direct URL hit on the sequence guard, or no in-flight wizard app at all,
 * redirects to the verdict screen rather than erroring (ux spec §6.2). */
const DirectionsRedirect = ({ appId }: { readonly appId: string | null }): React.JSX.Element => {
  const navigate = useNavigate();
  useEffect(() => {
    void navigate(appId === null ? "/apps/new" : "/apps/new/glim/verdict", { replace: true });
  }, [appId, navigate]);
  return <FocusedFlowShell title={buildCopy("build.brand.directions.title")}>{null}</FocusedFlowShell>;
};
